/**
 * Замок карточки байка (§2.2.2): «Открыть»/«Закрыть», блокировка в аренде,
 * перепроверка после «Закрыть».
 *
 * ⚠ ЭТА ЧАСТЬ КАРТОЧКИ НЕ РАБОТАЕТ БЕЗ СЕТИ, и не по недосмотру. Команда идёт
 * скаут → BFF → iot-gateway → живая TCP-сессия с замком. В офлайн-очередь
 * такое класть нельзя: «открыть» через сорок минут, когда скаут уже уехал, —
 * это открытый бесхозный байк на улице (см. `api/device.ts`).
 *
 * ⚠ ПЕРЕПРОВЕРКА ПОСЛЕ «ЗАКРЫТЬ» ЧЕРЕЗ ЯВНЫЙ ОПРОС, А НЕ ОТВЕТ КОМАНДЫ.
 * `success` у команды означает «замок принял радиокоманду», а не «дужка
 * физически защёлкнулась» — то же различие, из-за которого шапка карточки
 * читает состояние замка из heartbeat, а не отсюда. Через 7 секунд (в
 * пределах названных спецификацией 5–10 с) карточка спрашивает замок ещё
 * раз (`useScoutDevice`, тот же опрос, что раньше был кнопкой «Опросить») и,
 * если он всё ещё открыт, показывает это как отдельное предупреждение.
 */
import { useEffect, useRef, useState } from 'react'
import {
  commandBlockedReason,
  commandErrorText,
  commandLabel,
  isCommandOk,
  useScoutDevice,
  useScoutVehicleCommand,
  type DeviceCommand,
} from '@/api/device'
import { BigButton, Notice } from '@/components/ui'

/** Сколько ждать перед перепроверкой замка после «Закрыть». Середина 5–10 с. */
const RECHECK_DELAY_MS = 7000

type RecheckState = 'idle' | 'waiting' | 'stuck' | 'confirmed'

export function BikeLock({
  vehicleId,
  online,
  /** «В аренде» и другие причины, по которым замком нельзя пользоваться — до общей проверки сети. */
  rentalBlocked,
}: {
  vehicleId: string
  online: boolean
  rentalBlocked: string | null
}) {
  const command = useScoutVehicleCommand(vehicleId)
  const device = useScoutDevice(vehicleId, false)
  const [lastCmd, setLastCmd] = useState<{ cmd: DeviceCommand; status: string } | null>(null)
  const [recheck, setRecheck] = useState<RecheckState>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ⚠ ТАЙМЕР ОБЯЗАН ОСТАНАВЛИВАТЬСЯ. Без этого уход с карточки в течение
  // 7 секунд после «Закрыть» всё равно поднимает опрос устройства для
  // экрана, который уже никто не смотрит, — тот же принцип, что
  // `lib/scanner/camera.ts` формулирует для треков камеры.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const blocked = rentalBlocked ?? commandBlockedReason(online, vehicleId)

  function run(cmd: DeviceCommand) {
    setLastCmd(null)
    if (timer.current) clearTimeout(timer.current)
    command.mutate(cmd, {
      onSuccess: (out) => {
        setLastCmd(out)
        if (cmd !== 'lock') return
        setRecheck('waiting')
        timer.current = setTimeout(() => {
          void device.refetch().then((res) => {
            setRecheck(res.data && !res.data.locked ? 'stuck' : 'confirmed')
          })
        }, RECHECK_DELAY_MS)
      },
    })
  }

  return (
    <section className="mb-4">
      {blocked ? (
        <div className="mb-2">
          <Notice tone="warn">{blocked}</Notice>
        </div>
      ) : null}

      <div className="flex gap-2">
        <BigButton
          tone="primary"
          disabled={!!blocked}
          busy={command.isPending && command.variables === 'unlock'}
          onClick={() => {
            run('unlock')
          }}
        >
          Открыть
        </BigButton>
        <BigButton
          tone="neutral"
          disabled={!!blocked}
          busy={command.isPending && command.variables === 'lock'}
          onClick={() => {
            run('lock')
          }}
        >
          Закрыть
        </BigButton>
      </div>

      {command.isError ? (
        <div className="mt-2">
          <Notice tone="error">{commandErrorText(command.error)}</Notice>
        </div>
      ) : null}

      {lastCmd ? (
        <div className="mt-2">
          <Notice tone={isCommandOk(lastCmd.status) ? 'ok' : 'warn'}>
            {commandLabel(lastCmd.status)}
          </Notice>
        </div>
      ) : null}

      {recheck === 'waiting' ? (
        <p className="mt-2 text-xs opacity-60">Проверяем, защёлкнулся ли замок…</p>
      ) : null}
      {recheck === 'stuck' ? (
        <div className="mt-2">
          <Notice tone="warn">Замок не защёлкнулся — дёрни дужку.</Notice>
        </div>
      ) : null}
    </section>
  )
}
